// mds-dojo — dojo/fx — FAITHFUL port of references/dojo/fx.js. Effects on top of
// the base Animation engine: chain (sequence), combine (parallel), wipeIn, wipeOut,
// slideTo. AMD → ESM.
//
// Faithful adaptations (flagged):
//  - The `if(!dojo.isAsync){ ready(0, () => require(["./fx/Toggler"])) }` back-compat
//    block is DROPPED (along with the dojo/ready + require imports). It only lazily
//    pulled dojo/fx/Toggler into the global for 1.x back-compat; not needed under ESM.
//  - geom.position(n, true) (includeScroll) → mds dom-geometry.position ignores the
//    2nd arg and returns viewport-relative coords via getBoundingClientRect. Adequate
//    for slideTo on evergreen browsers.
import lang from './_base/lang';
import Evented from './Evented';
import dojo from './_base/kernel';
import arrayUtil from './_base/array';
import aspect from './aspect';
import baseFx from './_base/fx';
import dom from './dom';
import domStyle from './dom-style';
import geom from './dom-geometry';

var coreFx: any = (dojo as any).fx = {
  // summary:
  //		Effects library on top of Base animations
};

var _baseObj: any = {
  _fire: function (this: any, evt: any, args: any) {
    if (this[evt]) {
      this[evt].apply(this, args || []);
    }
    return this;
  }
};

var _chain: any = function (this: any, animations: any) {
  this._index = -1;
  this._animations = animations || [];
  this._current = this._onAnimateCtx = this._onEndCtx = null;

  this.duration = 0;
  arrayUtil.forEach(this._animations, function (this: any, a: any) {
    if (a) {
      if (typeof a.duration != 'undefined') {
        this.duration += a.duration;
      }
      if (a.delay) {
        this.duration += a.delay;
      }
    }
  }, this);
};
_chain.prototype = new (Evented as any)();
lang.extend(_chain, {
  _onAnimate: function (this: any) {
    this._fire('onAnimate', arguments);
  },
  _onEnd: function (this: any) {
    this._onAnimateCtx.remove();
    this._onEndCtx.remove();
    this._onAnimateCtx = this._onEndCtx = null;
    if (this._index + 1 == this._animations.length) {
      this._fire('onEnd');
    } else {
      // switch animations
      this._current = this._animations[++this._index];
      this._onAnimateCtx = aspect.after(this._current, 'onAnimate', lang.hitch(this, '_onAnimate'), true);
      this._onEndCtx = aspect.after(this._current, 'onEnd', lang.hitch(this, '_onEnd'), true);
      this._current.play(0, true);
    }
  },
  play: function (this: any, delay: any, gotoStart: any) {
    if (!this._current) { this._current = this._animations[this._index = 0]; }
    if (!gotoStart && this._current.status() == 'playing') { return this; }
    var beforeBegin = aspect.after(this._current, 'beforeBegin', lang.hitch(this, function (this: any) {
      this._fire('beforeBegin');
    }), true),
      onBegin = aspect.after(this._current, 'onBegin', lang.hitch(this, function (this: any, arg: any) {
        this._fire('onBegin', arguments);
      }), true),
      onPlay = aspect.after(this._current, 'onPlay', lang.hitch(this, function (this: any, arg: any) {
        this._fire('onPlay', arguments);
        beforeBegin.remove();
        onBegin.remove();
        onPlay.remove();
      }));
    if (this._onAnimateCtx) {
      this._onAnimateCtx.remove();
    }
    this._onAnimateCtx = aspect.after(this._current, 'onAnimate', lang.hitch(this, '_onAnimate'), true);
    if (this._onEndCtx) {
      this._onEndCtx.remove();
    }
    this._onEndCtx = aspect.after(this._current, 'onEnd', lang.hitch(this, '_onEnd'), true);
    this._current.play.apply(this._current, arguments);
    return this;
  },
  pause: function (this: any) {
    if (this._current) {
      var e = aspect.after(this._current, 'onPause', lang.hitch(this, function (this: any, arg: any) {
        this._fire('onPause', arguments);
        e.remove();
      }), true);
      this._current.pause();
    }
    return this;
  },
  gotoPercent: function (this: any, percent: any, andPlay: any) {
    this.pause();
    var offset = this.duration * percent;
    this._current = null;

    arrayUtil.some(this._animations, function (this: any, a: any, index: any) {
      if (offset <= a.duration) {
        this._current = a;
        this._index = index;
        return true;
      }
      offset -= a.duration;
      return false;
    }, this);
    if (this._current) {
      this._current.gotoPercent(offset / this._current.duration);
    }
    if (andPlay) { this.play(); }
    return this;
  },
  stop: function (this: any, gotoEnd: any) {
    if (this._current) {
      if (gotoEnd) {
        for (; this._index + 1 < this._animations.length; ++this._index) {
          this._animations[this._index].stop(true);
        }
        this._current = this._animations[this._index];
      }
      var e = aspect.after(this._current, 'onStop', lang.hitch(this, function (this: any, arg: any) {
        this._fire('onStop', arguments);
        e.remove();
      }), true);
      this._current.stop();
    }
    return this;
  },
  status: function (this: any) {
    return this._current ? this._current.status() : 'stopped';
  },
  destroy: function (this: any) {
    this.stop();
    if (this._onAnimateCtx) { this._onAnimateCtx.remove(); }
    if (this._onEndCtx) { this._onEndCtx.remove(); }
  }
});
lang.extend(_chain, _baseObj);

coreFx.chain = function (animations: any) {
  // summary:
  //		Chain a list of `dojo/_base/fx.Animation`s to run in sequence
  return new _chain(lang.isArray(animations) ? animations : Array.prototype.slice.call(animations, 0));
};

var _combine: any = function (this: any, animations: any) {
  this._animations = animations || [];
  this._connects = [];
  this._finished = 0;

  this.duration = 0;
  arrayUtil.forEach(animations, function (this: any, a: any) {
    var duration = a.duration;
    if (a.delay) { duration += a.delay; }
    if (this.duration < duration) { this.duration = duration; }
    this._connects.push(aspect.after(a, 'onEnd', lang.hitch(this, '_onEnd'), true));
  }, this);

  this._pseudoAnimation = new (baseFx as any).Animation({ curve: [0, 1], duration: this.duration });
  var self = this;
  arrayUtil.forEach(['beforeBegin', 'onBegin', 'onPlay', 'onAnimate', 'onPause', 'onStop', 'onEnd'],
    function (evt: any) {
      self._connects.push(aspect.after(self._pseudoAnimation, evt,
        function () { self._fire(evt, arguments); },
        true));
    }
  );
};
lang.extend(_combine, {
  _doAction: function (this: any, action: any, args: any) {
    arrayUtil.forEach(this._animations, function (a: any) {
      a[action].apply(a, args);
    });
    return this;
  },
  _onEnd: function (this: any) {
    if (++this._finished > this._animations.length) {
      this._fire('onEnd');
    }
  },
  _call: function (this: any, action: any, args: any) {
    var t = this._pseudoAnimation;
    t[action].apply(t, args);
  },
  play: function (this: any, delay: any, gotoStart: any) {
    this._finished = 0;
    this._doAction('play', arguments);
    this._call('play', arguments);
    return this;
  },
  pause: function (this: any) {
    this._doAction('pause', arguments);
    this._call('pause', arguments);
    return this;
  },
  gotoPercent: function (this: any, percent: any, andPlay: any) {
    var ms = this.duration * percent;
    arrayUtil.forEach(this._animations, function (a: any) {
      a.gotoPercent(a.duration < ms ? 1 : (ms / a.duration), andPlay);
    });
    this._call('gotoPercent', arguments);
    return this;
  },
  stop: function (this: any, gotoEnd: any) {
    this._doAction('stop', arguments);
    this._call('stop', arguments);
    return this;
  },
  status: function (this: any) {
    return this._pseudoAnimation.status();
  },
  destroy: function (this: any) {
    this.stop();
    arrayUtil.forEach(this._connects, function (handle: any) {
      handle.remove();
    });
  }
});
lang.extend(_combine, _baseObj);

coreFx.combine = function (animations: any) {
  // summary:
  //		Combine a list of `dojo/_base/fx.Animation`s to run in parallel
  return new _combine(lang.isArray(animations) ? animations : Array.prototype.slice.call(animations, 0));
};

coreFx.wipeIn = function (args: any) {
  // summary:
  //		Expand a node to it's natural height.

  var node = args.node = dom.byId(args.node), s = node.style, o: any;

  var anim = (baseFx as any).animateProperty(lang.mixin({
    properties: {
      height: {
        // wrapped in functions so we wait till the last second to query (in case value has changed)
        start: function () {
          // start at current [computed] height, but use 1px rather than 0
          // because 0 causes IE to display the whole panel
          o = s.overflow;
          s.overflow = 'hidden';
          if (s.visibility == 'hidden' || s.display == 'none') {
            s.height = '1px';
            s.display = '';
            s.visibility = '';
            return 1;
          } else {
            var height = domStyle.get(node, 'height');
            return Math.max(height, 1);
          }
        },
        end: function () {
          return node.scrollHeight;
        }
      }
    }
  }, args));

  var fini = function () {
    s.height = 'auto';
    s.overflow = o;
  };
  aspect.after(anim, 'onStop', fini, true);
  aspect.after(anim, 'onEnd', fini, true);

  return anim; // dojo/_base/fx.Animation
};

coreFx.wipeOut = function (args: any) {
  // summary:
  //		Shrink a node to nothing and hide it.

  var node = args.node = dom.byId(args.node), s = node.style, o: any;

  var anim = (baseFx as any).animateProperty(lang.mixin({
    properties: {
      height: {
        end: 1 // 0 causes IE to display the whole panel
      }
    }
  }, args));

  aspect.after(anim, 'beforeBegin', function () {
    o = s.overflow;
    s.overflow = 'hidden';
    s.display = '';
  }, true);
  var fini = function () {
    s.overflow = o;
    s.height = 'auto';
    s.display = 'none';
  };
  aspect.after(anim, 'onStop', fini, true);
  aspect.after(anim, 'onEnd', fini, true);

  return anim; // dojo/_base/fx.Animation
};

coreFx.slideTo = function (args: any) {
  // summary:
  //		Slide a node to a new top/left position

  var node = args.node = dom.byId(args.node),
    top: any = null, left: any = null;

  var init = (function (n) {
    return function () {
      var cs = domStyle.getComputedStyle(n);
      var pos = cs.position;
      top = (pos == 'absolute' ? n.offsetTop : parseInt(cs.top) || 0);
      left = (pos == 'absolute' ? n.offsetLeft : parseInt(cs.left) || 0);
      if (pos != 'absolute' && pos != 'relative') {
        var ret = (geom as any).position(n, true);
        top = ret.y;
        left = ret.x;
        n.style.position = 'absolute';
        n.style.top = top + 'px';
        n.style.left = left + 'px';
      }
    };
  })(node);
  init();

  var anim = (baseFx as any).animateProperty(lang.mixin({
    properties: {
      top: args.top || 0,
      left: args.left || 0
    }
  }, args));
  aspect.after(anim, 'beforeBegin', init, true);

  return anim; // dojo/_base/fx.Animation
};

export default coreFx;
